/**
 * Date-range presets (EP05-S09, `13/10` §3): Today, Yesterday, This Week, Last
 * Week, This Month, Last Month, Last 7 Days, Last 30 Days, and a custom range.
 *
 * **Dates, not instants.** A range is two calendar dates, `YYYY-MM-DD`, both
 * inclusive, in the organisation's clock; the server turns them into UTC
 * bounds at its midnight. So everything here is calendar arithmetic on a
 * date — never hours added to a `Date`, which a daylight-saving change would
 * shift by one.
 *
 * **Weeks start on Monday** (ISO 8601), the working week in Lebanon.
 */

export const DATE_RANGE_PRESETS = [
	"today",
	"yesterday",
	"thisWeek",
	"lastWeek",
	"thisMonth",
	"lastMonth",
	"last7",
	"last30",
] as const;

export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number] | "custom";

export interface DateRange {
	from: string;
	to: string;
}

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Whether a string is a real calendar date, `YYYY-MM-DD`. */
export function isIsoDate(value: string): boolean {
	const match = DATE.exec(value);
	if (match === null) {
		return false;
	}
	const [, y, m, d] = match.map(Number) as [number, number, number, number];
	// An impossible day (31 September, 00) rolls into another month, so the
	// month alone tells a real date from one.
	const date = new Date(Date.UTC(y, m - 1, d));
	return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1;
}

/** A date as a UTC-midnight `Date`, for calendar arithmetic only. */
function parse(value: string): Date {
	const [y, m, d] = value.split("-").map(Number) as [number, number, number];
	return new Date(Date.UTC(y, m - 1, d));
}

function format(date: Date): string {
	return date.toISOString().slice(0, 10);
}

/** The date `days` after (or before, when negative) a date. */
export function addDays(value: string, days: number): string {
	const date = parse(value);
	date.setUTCDate(date.getUTCDate() + days);
	return format(date);
}

/** Today's date in a timezone, from the device's instant. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
	// en-CA writes dates as YYYY-MM-DD, whatever the viewer's own locale.
	return new Intl.DateTimeFormat("en-CA", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).format(now);
}

/** The range a preset means on a given day. */
export function presetRange(preset: Exclude<DateRangePreset, "custom">, today: string): DateRange {
	const date = parse(today);
	// Monday is 0: getUTCDay() is Sunday 0 … Saturday 6.
	const sinceMonday = (date.getUTCDay() + 6) % 7;
	const monday = addDays(today, -sinceMonday);
	const firstOfMonth = format(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)));
	switch (preset) {
		case "today":
			return { from: today, to: today };
		case "yesterday": {
			const yesterday = addDays(today, -1);
			return { from: yesterday, to: yesterday };
		}
		case "thisWeek":
			return { from: monday, to: today };
		case "lastWeek":
			return { from: addDays(monday, -7), to: addDays(monday, -1) };
		case "thisMonth":
			return { from: firstOfMonth, to: today };
		case "lastMonth": {
			const lastOfPrevious = addDays(firstOfMonth, -1);
			return { from: `${lastOfPrevious.slice(0, 8)}01`, to: lastOfPrevious };
		}
		case "last7":
			return { from: addDays(today, -6), to: today };
		case "last30":
			return { from: addDays(today, -29), to: today };
	}
}

/**
 * Which preset a range is on a given day, or `custom` — so a range typed by
 * hand that happens to be last week reads as Last Week.
 */
export function presetOf(range: DateRange, today: string): DateRangePreset {
	for (const preset of DATE_RANGE_PRESETS) {
		const candidate = presetRange(preset, today);
		if (candidate.from === range.from && candidate.to === range.to) {
			return preset;
		}
	}
	return "custom";
}
